import { useEffect, useRef, useState } from "react";
import { X, Mic, Send, Sparkles, ShieldCheck, PhoneCall, Loader2, StopCircle, RotateCcw, FlaskConical, CheckCircle2, Clock, ThumbsUp, ThumbsDown } from "lucide-react";
import { Link } from "react-router-dom";
import {
  publicDemoInfo, publicDemoStart, publicDemoChat, publicDemoVoice,
  publicDemoLead, publicDemoEnd,
  litlabAgentInfo, litlabAgentStart, litlabAgentLead, litlabAgentEnd,
  litlabAgentQuoteSubmit, litlabAgentQuoteGet, litlabAgentCsat, litlabAgentVoiceTour,
  litlabAgentCsatVoicePrompt, litlabAgentCsatVoice, litlabAgentAsk,
} from "@/lib/api";

// Variant profiles — different endpoints, branding, session cap.
const VARIANTS = {
  websitetech: {
    infoFn: publicDemoInfo,
    startFn: publicDemoStart,
    leadFn: publicDemoLead,
    endFn: publicDemoEnd,
    accentGradient: "from-indigo-500 to-fuchsia-500",
    headerLabel: "WebsiteTech.pro AI Employee · grounded in our own knowledge",
    inputPlaceholder: "Ask anything about WebsiteTech.pro…",
    IconComp: Sparkles,
    // Light-theme tokens — brand purple for WebsiteTech
    theme: {
      shell:       "bg-[#F5F3FF]",             // lavender-50
      shellBorder: "border-indigo-200",
      headerBg:    "bg-gradient-to-r from-indigo-600 via-indigo-500 to-fuchsia-500 text-white",
      headerSub:   "text-indigo-100",
      consentBg:   "bg-indigo-50 text-indigo-900 border-indigo-200",
      bodyBg:      "bg-white",
      panelCard:   "bg-white border-indigo-100 hover:border-indigo-400 text-slate-800",
      chatUser:    "bg-indigo-600 text-white",
      chatAssistant: "bg-white border border-indigo-100 text-slate-800",
      inputBg:     "bg-white border-indigo-200 text-slate-900 placeholder-slate-400",
      inputWrap:   "bg-white border-t border-indigo-100",
    },
  },
  litlab: {
    infoFn: litlabAgentInfo,
    startFn: litlabAgentStart,
    leadFn: litlabAgentLead,
    endFn: litlabAgentEnd,
    accentGradient: "from-sky-500 to-emerald-500",
    headerLabel: "L.I.T. Labs AI Assistant · grounded in our services + accreditations",
    inputPlaceholder: "Ask Iris about our testing services…",
    IconComp: FlaskConical,
    // Light-theme tokens — brand green/sky for LitLab
    theme: {
      shell:       "bg-[#ECFEFF]",             // cyan-50
      shellBorder: "border-sky-200",
      headerBg:    "bg-gradient-to-r from-sky-600 via-sky-500 to-emerald-500 text-white",
      headerSub:   "text-sky-100",
      consentBg:   "bg-emerald-50 text-emerald-900 border-emerald-200",
      bodyBg:      "bg-white",
      panelCard:   "bg-white border-sky-100 hover:border-sky-400 text-slate-800",
      chatUser:    "bg-sky-600 text-white",
      chatAssistant: "bg-white border border-sky-100 text-slate-800",
      inputBg:     "bg-white border-sky-200 text-slate-900 placeholder-slate-400",
      inputWrap:   "bg-white border-t border-sky-100",
    },
  },
};


// Parse "<QUOTE_INTENT>{...}</QUOTE_INTENT>" out of Iris' reply and return
// {clean, intent}. Returns null intent if none present or JSON invalid.
function parseQuoteIntent(text) {
  if (!text || typeof text !== "string") return { clean: text || "", intent: null };
  const m = text.match(/<QUOTE_INTENT>([\s\S]*?)<\/QUOTE_INTENT>/i);
  if (!m) return { clean: text, intent: null };
  let parsed = null;
  try { parsed = JSON.parse(m[1].trim()); } catch { parsed = null; }
  const clean = text.replace(/<QUOTE_INTENT>[\s\S]*?<\/QUOTE_INTENT>/i, "").trim();
  return { clean, intent: parsed };
}

function SlaCountdown({ deadlineIso, lang, quoteId }) {
  const [now, setNow] = useState(Date.now());
  const [opened, setOpened] = useState(null);
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(tick);
  }, []);
  // Poll the quote every 20s to catch metallurgist "opened" state
  useEffect(() => {
    if (!quoteId) return;
    let alive = true;
    const poll = async () => {
      try {
        const q = await litlabAgentQuoteGet(quoteId);
        if (alive && q?.opened_at) setOpened(q);
      } catch { /* ignore */ }
    };
    poll();
    const id = setInterval(poll, 20_000);
    return () => { alive = false; clearInterval(id); };
  }, [quoteId]);

  const T = {
    en: { replying: "We'll reply within", opened: "A metallurgist opened your quote.", overdue: "Working on it now — expect a reply shortly." },
    es: { replying: "Te responderemos en", opened: "Un metalurgista abrió tu cotización.", overdue: "Estamos trabajando en ello — recibirás respuesta pronto." },
    fr: { replying: "Réponse sous", opened: "Un métallurgiste a ouvert votre devis.", overdue: "Nous y travaillons — réponse imminente." },
  }[lang] || null;
  const t = T || { replying: "We'll reply within", opened: "A metallurgist opened your quote.", overdue: "Working on it now — expect a reply shortly." };

  if (opened) {
    return (
      <div className="rounded-lg bg-indigo-900/50 border border-indigo-500/40 px-3 py-2 flex items-center gap-2 text-xs text-indigo-100" data-testid="sla-opened">
        <ShieldCheck className="w-3.5 h-3.5 text-indigo-300" />
        <div>
          <div className="font-semibold">{t.opened}</div>
          <div className="text-indigo-300/80">
            {new Date(opened.opened_at).toLocaleString()}
          </div>
        </div>
      </div>
    );
  }
  const deadline = new Date(deadlineIso).getTime();
  const ms = Math.max(0, deadline - now);
  const overdue = ms === 0;
  const hh = Math.floor(ms / 3600_000);
  const mm = Math.floor((ms % 3600_000) / 60_000);
  return (
    <div className="rounded-lg bg-slate-900/60 border border-white/10 px-3 py-2 flex items-center gap-2 text-xs" data-testid="sla-countdown">
      <Clock className="w-3.5 h-3.5 text-emerald-300" />
      <div className="text-slate-200">
        {overdue ? (
          <span className="text-amber-200">{t.overdue}</span>
        ) : (
          <>
            <span className="text-slate-400">{t.replying}</span>{" "}
            <span className="font-mono font-bold text-white">
              {hh}h {mm.toString().padStart(2, "0")}m
            </span>
          </>
        )}
      </div>
    </div>
  );
}


function CsatWidget({ quoteId, lang, autoVoice = false }) {
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [showComment, setShowComment] = useState(false);
  // Voice CSAT state
  const [voicePhase, setVoicePhase] = useState("idle"); // idle|prompt|recording|processing
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [voiceError, setVoiceError] = useState("");
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const autoStartedRef = useRef(false);

  const T = {
    en: { prompt: "How was Iris?", thanks: "Thanks for the feedback!", commentPh: "What could be better? (optional)", submit: "Send",
           voiceCta: "Reply by voice", voicePrompt: "How was that experience?", listening: "Listening…", processing: "Processing your reply…", tryText: "Or use thumbs" },
    es: { prompt: "¿Qué tal Iris?", thanks: "¡Gracias por tus comentarios!", commentPh: "¿Qué podríamos mejorar? (opcional)", submit: "Enviar",
           voiceCta: "Responder por voz", voicePrompt: "¿Cómo fue tu experiencia?", listening: "Escuchando…", processing: "Procesando tu respuesta…", tryText: "O usa los pulgares" },
    fr: { prompt: "Comment était Iris ?", thanks: "Merci pour vos retours !", commentPh: "Que pouvons-nous améliorer ? (facultatif)", submit: "Envoyer",
           voiceCta: "Répondre à la voix", voicePrompt: "Comment était votre expérience ?", listening: "Écoute…", processing: "Traitement de votre réponse…", tryText: "Ou utilisez les pouces" },
  }[lang] || null;
  const t = T || { prompt: "How was Iris?", thanks: "Thanks for the feedback!", commentPh: "What could be better? (optional)", submit: "Send",
                    voiceCta: "Reply by voice", voicePrompt: "How was that experience?", listening: "Listening…", processing: "Processing your reply…", tryText: "Or use thumbs" };

  const submit = async (r) => {
    if (sending || sent) return;
    setSending(true); setRating(r);
    try {
      await litlabAgentCsat({ quote_id: quoteId, rating: r, comment: comment || null });
      if (r === "up" || comment) setSent(true);
      else setShowComment(true);
    } catch { /* ignore */ } finally { setSending(false); }
  };
  const sendComment = async () => {
    setSending(true);
    try {
      await litlabAgentCsat({ quote_id: quoteId, rating: rating || "down", comment: comment || null });
      setSent(true);
    } catch { /* ignore */ } finally { setSending(false); }
  };

  const cleanupRecorder = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    try { mrRef.current?.stop(); } catch {}
    mrRef.current = null;
  };

  const startVoiceCsat = async () => {
    if (voicePhase !== "idle" || sent) return;
    setVoiceError("");
    setVoicePhase("prompt");
    // 1. Play the TTS prompt
    try {
      const tour = await litlabAgentCsatVoicePrompt(lang || "en");
      if (tour?.audio_base64) {
        const audio = new Audio(`data:${tour.mime || "audio/mpeg"};base64,${tour.audio_base64}`);
        audioRef.current = audio;
        await new Promise((resolve) => {
          audio.onended = resolve;
          audio.onerror = resolve;
          audio.play().catch(resolve);
        });
      }
    } catch { /* if TTS fails, still record */ }
    // 2. Start recording for 10s
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        setVoicePhase("processing");
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size < 300) {
          setVoiceError("No speech detected — please try the thumbs.");
          setVoicePhase("idle");
          return;
        }
        const b64 = await blob.arrayBuffer().then((b) =>
          btoa(new Uint8Array(b).reduce((a, x) => a + String.fromCharCode(x), ""))
        );
        try {
          const res = await litlabAgentCsatVoice({
            quote_id: quoteId, audio_base64: b64,
            mime: mr.mimeType || "audio/webm", language: lang || "en",
          });
          if (res.skipped) {
            setVoiceError("No speech detected — please try the thumbs.");
            setVoicePhase("idle");
            return;
          }
          setRating(res.rating || "up");
          setVoiceTranscript(res.transcript || "");
          setSent(true);
        } catch (e) {
          setVoiceError(e?.response?.data?.detail || "Voice reply failed — please use the thumbs.");
          setVoicePhase("idle");
        }
      };
      mrRef.current = mr;
      mr.start();
      setVoicePhase("recording");
      // Auto-stop after 10s
      timerRef.current = setTimeout(() => {
        try { mrRef.current?.stop(); } catch {}
      }, 10_000);
    } catch {
      setVoiceError("Mic permission denied — please use the thumbs.");
      setVoicePhase("idle");
    }
  };

  const stopVoiceEarly = () => { cleanupRecorder(); };

  useEffect(() => () => cleanupRecorder(), []);
  useEffect(() => {
    if (autoVoice && !autoStartedRef.current && !sent && voicePhase === "idle") {
      autoStartedRef.current = true;
      // Small delay so the "Quote submitted" card is visible first
      const id = setTimeout(() => { startVoiceCsat(); }, 900);
      return () => clearTimeout(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoVoice, sent]);

  if (sent) return (
    <div className="text-xs text-emerald-200 flex items-center gap-1 flex-wrap" data-testid="csat-thanks">
      <CheckCircle2 className="w-3 h-3" /> {t.thanks}
      {voiceTranscript && (
        <span className="text-emerald-300/70 italic ml-1">“{voiceTranscript}”</span>
      )}
    </div>
  );

  // Voice-active states
  if (voicePhase === "prompt" || voicePhase === "recording" || voicePhase === "processing") {
    return (
      <div className="rounded-lg bg-slate-900/60 border border-emerald-500/30 px-3 py-2 flex items-center gap-2"
        data-testid={`csat-voice-${voicePhase}`}>
        {voicePhase === "recording" ? (
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
          </span>
        ) : (
          <Loader2 className="w-3.5 h-3.5 text-emerald-300 animate-spin" />
        )}
        <div className="text-xs text-slate-200 flex-1">
          {voicePhase === "recording" && t.listening}
          {voicePhase === "prompt" && t.voicePrompt}
          {voicePhase === "processing" && t.processing}
        </div>
        {voicePhase === "recording" && (
          <button onClick={stopVoiceEarly} data-testid="csat-voice-stop"
            className="text-[10px] font-bold uppercase text-emerald-300 hover:text-emerald-100 tracking-wider">Done</button>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-slate-900/50 border border-white/10 px-3 py-2 space-y-2" data-testid="csat-widget">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-slate-300">{t.prompt}</div>
        <div className="flex gap-1">
          <button onClick={() => submit("up")} disabled={sending}
            data-testid="csat-up"
            className={`w-8 h-8 grid place-items-center rounded-full transition ${rating === "up" ? "bg-emerald-500 text-white" : "bg-white/5 hover:bg-emerald-500/20 text-emerald-300"} disabled:opacity-50`}>
            <ThumbsUp className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => submit("down")} disabled={sending}
            data-testid="csat-down"
            className={`w-8 h-8 grid place-items-center rounded-full transition ${rating === "down" ? "bg-rose-500 text-white" : "bg-white/5 hover:bg-rose-500/20 text-rose-300"} disabled:opacity-50`}>
            <ThumbsDown className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <button onClick={startVoiceCsat}
        data-testid="csat-voice-start"
        className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-emerald-300 hover:text-emerald-100 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded px-2 py-1.5 transition">
        <Mic className="w-3 h-3" /> {t.voiceCta}
      </button>
      {voiceError && (
        <div className="text-[10px] text-amber-300" data-testid="csat-voice-error">{voiceError}</div>
      )}
      {showComment && !sent && (
        <div className="flex gap-1">
          <input value={comment} onChange={(e) => setComment(e.target.value)}
            data-testid="csat-comment" placeholder={t.commentPh}
            className="flex-1 bg-slate-800 text-white text-xs rounded px-2 py-1.5 outline-none border border-white/10 focus:border-emerald-500" />
          <button onClick={sendComment} disabled={sending} data-testid="csat-comment-send"
            className="text-xs font-bold text-white bg-emerald-500 hover:bg-emerald-400 rounded px-2 py-1.5 disabled:opacity-50">{t.submit}</button>
        </div>
      )}
    </div>
  );
}


function QuoteConfirmCard({ intent, submitting, submitted, error, onEdit, onSubmit, lang, voiceMode = false }) {
  const T = {
    en: { title: "Ready to submit your quote request?", confirmed: "Quote request submitted.", submit: "Yes, submit", editing: "Edit fields", industry: "Industry", services: "Testing", samples: "Samples", material: "Material", turnaround: "Turnaround", deadline: "Deadline", contact: "Contact", notes: "Notes" },
    es: { title: "¿Enviar tu solicitud de cotización?", confirmed: "Solicitud enviada.", submit: "Sí, enviar", editing: "Editar campos", industry: "Industria", services: "Ensayos", samples: "Muestras", material: "Material", turnaround: "Plazo", deadline: "Fecha límite", contact: "Contacto", notes: "Notas" },
    fr: { title: "Envoyer votre demande de devis ?", confirmed: "Demande envoyée.", submit: "Oui, envoyer", editing: "Modifier", industry: "Industrie", services: "Essais", samples: "Échantillons", material: "Matériau", turnaround: "Délai", deadline: "Date limite", contact: "Contact", notes: "Notes" },
  }[lang] || null;
  const t = T || { title: "Ready to submit your quote request?", confirmed: "Quote request submitted.", submit: "Yes, submit", editing: "Edit fields", industry: "Industry", services: "Testing", samples: "Samples", material: "Material", turnaround: "Turnaround", deadline: "Deadline", contact: "Contact", notes: "Notes" };
  const row = (label, value) => value ? (
    <div className="flex gap-2 text-xs">
      <div className="text-slate-400 uppercase tracking-wider w-24 flex-shrink-0">{label}</div>
      <div className="text-slate-100 break-words">{Array.isArray(value) ? value.join(", ") : String(value)}</div>
    </div>
  ) : null;
  if (submitted) {
    return (
      <div className="rounded-xl bg-emerald-900/40 border border-emerald-500/40 p-4 space-y-2" data-testid="quote-confirmed">
        <div className="flex items-center gap-2 text-emerald-300 font-semibold text-sm">
          <CheckCircle2 className="w-4 h-4" /> {submitted.message || t.confirmed}
        </div>
        {submitted.reference && (
          <div className="mt-1 text-xs text-slate-300 font-mono">Ref: #{submitted.reference}</div>
        )}
        {submitted.sla_deadline_at && (
          <SlaCountdown deadlineIso={submitted.sla_deadline_at} lang={lang} quoteId={submitted.quote_id} />
        )}
        {submitted.quote_id && (
          <CsatWidget quoteId={submitted.quote_id} lang={lang} autoVoice={voiceMode} />
        )}
      </div>
    );
  }
  return (
    <div className="rounded-xl bg-gradient-to-br from-sky-950 to-emerald-950 border border-sky-500/40 p-4 space-y-2" data-testid="quote-intent-card">
      <div className="text-sm font-bold text-white">{t.title}</div>
      <div className="space-y-1 mt-2">
        {row("Name", intent.name)}
        {row(t.contact, [intent.email, intent.phone].filter(Boolean).join(" · "))}
        {row("Company", intent.company)}
        {row(t.industry, intent.industry)}
        {row(t.services, intent.services)}
        {row(t.samples, intent.sample_count)}
        {row(t.material, intent.material)}
        {row(t.turnaround, intent.turnaround)}
        {row(t.deadline, intent.deadline)}
        {row(t.notes, intent.notes)}
      </div>
      {error && <div className="text-xs text-rose-300 bg-rose-950/40 border border-rose-500/30 rounded p-2">{error}</div>}
      <div className="pt-2 flex gap-2 flex-wrap">
        <button onClick={onSubmit} disabled={submitting}
          data-testid="quote-submit-btn"
          className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white text-sm font-bold px-4 py-2 rounded-lg transition">
          {submitting ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> …</> : <><CheckCircle2 className="w-3.5 h-3.5" /> {t.submit}</>}
        </button>
        <button onClick={onEdit} disabled={submitting}
          data-testid="quote-edit-btn"
          className="inline-flex items-center gap-1.5 border border-white/20 hover:border-white/40 text-white/80 text-sm font-medium px-3 py-2 rounded-lg transition">
          {t.editing}
        </button>
      </div>
    </div>
  );
}

/* Public "Talk to Our AI Employee" demo modal.
 *
 * Reuses the existing Representative runtime — the same engine our customers
 * ship — but seeded with a public-demo persona grounded in the variant's
 * knowledge base. Guardrails:
 *   • Rate-limited per IP (server enforces)
 *   • Session cap (visible timer)
 *   • Voice audio processed live, never stored
 *   • Lead only captured on explicit "book a demo" intent, with consent
 */
export default function PublicVoiceDemoDialog({ open, onClose, variant = "websitetech" }) {
  const v = VARIANTS[variant] || VARIANTS.websitetech;
  const [info, setInfo] = useState(null);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  // One-time hint after the first successful voice reply so visitors realise
  // they can tap the mic again — persisted in localStorage so it never nags.
  const [showVoiceHint, setShowVoiceHint] = useState(false);
  const voiceHintTimerRef = useRef(null);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [remaining, setRemaining] = useState(180);
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: "", email: "", phone: "", interest: "", consent: true });
  const [leadStatus, setLeadStatus] = useState(null);
  const [error, setError] = useState(null);
  const [language, setLanguage] = useState("en");
  const [languagePicked, setLanguagePicked] = useState(false);
  // Voice: which language did Whisper detect on the most recent audio input?
  // Displayed next to the mic as a reassuring "Speaking English/Español/Français" pill.
  const [detectedLang, setDetectedLang] = useState(null);
  // Iris auto-quote flow (LitLab variant only)
  const [quoteState, setQuoteState] = useState({ submitting: false, submitted: null, error: null });
  // Track whether the most recent input turn was voice — used to auto-prompt
  // the CSAT question aloud after a voice-driven quote submission.
  const [lastInputWasVoice, setLastInputWasVoice] = useState(false);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const scrollRef = useRef(null);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  // Load info on open. Session starts AFTER visitor picks a language.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    setLeadStatus(null);
    setLanguagePicked(false);
    (async () => {
      try {
        const i = await v.infoFn();
        if (cancelled) return;
        setInfo(i);
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "Could not start demo";
        setError(msg);
      }
    })();
    return () => { cancelled = true; };
  }, [open, variant]);

  const startSessionInLanguage = async (code) => {
    if (!info) return;
    setLanguage(code);
    setLanguagePicked(true);
    setError(null);
    try {
      const s = await v.startFn({
        consent: true, language: code,
        referrer: window.location.pathname,
        user_agent: navigator.userAgent,
      });
      setSession(s);
      setRemaining(s.expires_in_seconds || 180);
      setMessages([{ role: "assistant", text: s.welcome || info.welcome, at: Date.now() }]);
      // Voice onboarding tour — LitLab variant, first visit only
      if (variant === "litlab" && info.voice_tour?.enabled && !localStorage.getItem("iris_tour_done")) {
        try {
          const tour = await litlabAgentVoiceTour(code);
          if (tour?.audio_base64) {
            const audio = new Audio(`data:${tour.mime || "audio/mpeg"};base64,${tour.audio_base64}`);
            audio.play().catch(() => {});
            localStorage.setItem("iris_tour_done", "1");
          }
        } catch { /* silent — tour is optional */ }
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Could not start demo";
      setError(msg);
      setLanguagePicked(false);
    }
  };

  // Countdown timer
  useEffect(() => {
    if (!open || !session) return;
    timerRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [open, session]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, voiceBusy]);

  // Cleanup on close
  useEffect(() => {
    if (open) return;
    if (session) { v.endFn(session.session_id).catch(() => {}); }
    if (mediaRef.current) { try { mediaRef.current.stop(); } catch {} mediaRef.current = null; }
    if (audioRef.current) { try { audioRef.current.pause(); } catch {} }
    setSession(null); setMessages([]); setDraft(""); setRecording(false);
    setVoiceBusy(false); setLeadOpen(false); setLeadStatus(null); setError(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const playReply = (audio) => {
    if (!audio?.audio_base64) return;
    try {
      const mime = audio.mime || "audio/mpeg";
      const url = `data:${mime};base64,${audio.audio_base64}`;
      if (audioRef.current) { try { audioRef.current.pause(); } catch {} }
      audioRef.current = new Audio(url);
      audioRef.current.play().catch(() => {});
      // Show a one-time "tap the mic again" hint after the first spoken reply.
      try {
        const seen = localStorage.getItem("wt_voice_retry_hint_seen");
        if (!seen) {
          audioRef.current.addEventListener("ended", () => {
            setShowVoiceHint(true);
            if (voiceHintTimerRef.current) clearTimeout(voiceHintTimerRef.current);
            voiceHintTimerRef.current = setTimeout(() => setShowVoiceHint(false), 4500);
            try { localStorage.setItem("wt_voice_retry_hint_seen", "1"); } catch {}
          }, { once: true });
        }
      } catch {}
    } catch {}
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? draft).trim();
    if (!text || sending || !session) return;
    setSending(true);
    setDraft("");
    setLastInputWasVoice(false);
    setMessages((m) => [...m, { role: "visitor", text, at: Date.now() }]);
    try {
      let reply, citations, actions, source;
      if (variant === "litlab") {
        // FAQ cache first (instant); LLM fallback saves the pair for next time
        const res = await litlabAgentAsk(text, session.session_id, {
          preferred_language: language,
        });
        reply = res.reply || "Let me connect you with a human.";
        citations = res.citations;
        actions = res.actions_offered;
        source = res.source;
      } else {
        const res = await publicDemoChat(session.rep_id, session.session_id, text, language);
        reply = res.reply || "Let me connect you with a human.";
        citations = res.citations;
        actions = res.actions_offered;
      }
      setMessages((m) => [...m, {
        role: "assistant", text: reply, at: Date.now(),
        citations, actions, source,
      }]);
      // Auto-open lead form when user mentions demo booking
      if (/book|schedule.*demo|talk to.*sales|book a call/i.test(text) && !leadStatus) {
        setLeadOpen(true);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Chat failed");
    } finally {
      setSending(false);
    }
  };

  const toggleMic = async () => {
    if (recording) {
      // stop
      try { mediaRef.current?.stop(); } catch {}
      setRecording(false);
      return;
    }
    // start — defensively reset state so a second/third recording still works
    setError(null);
    setVoiceBusy(false);  // clear any stuck busy flag from a prior failure
    // Silence any AI TTS reply still playing — some browsers refuse a fresh
    // getUserMedia while the tab is already emitting audio.
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch {}
      audioRef.current = null;
    }
    // Fully release any previous recorder + its stream before asking for a new one.
    if (mediaRef.current) {
      try {
        if (mediaRef.current.state !== "inactive") mediaRef.current.stop();
        mediaRef.current.stream?.getTracks?.().forEach((t) => { try { t.stop(); } catch {} });
      } catch {}
      mediaRef.current = null;
    }
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        setRecording(false);
        stream.getTracks().forEach((t) => { try { t.stop(); } catch {} });
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        // Free the ref so the next click starts truly fresh
        if (mediaRef.current === mr) mediaRef.current = null;
        if (blob.size < 200) { setError("Recording too short"); return; }
        const b64 = await blob.arrayBuffer().then((b) =>
          btoa(new Uint8Array(b).reduce((a, x) => a + String.fromCharCode(x), ""))
        );
        setVoiceBusy(true);
        try {
          const res = await publicDemoVoice(session.rep_id, session.session_id, b64, mr.mimeType || "audio/webm", language);
          if (res.transcript_language && res.transcript_language !== detectedLang) {
            setDetectedLang(res.transcript_language);
          }
          if (res.transcript) {
            setMessages((m) => [...m, { role: "visitor", text: res.transcript, at: Date.now(), voice: true, lang: res.transcript_language }]);
            setLastInputWasVoice(true);
          }
          if (res.reply) setMessages((m) => [...m, {
            role: "assistant", text: res.reply, at: Date.now(),
            citations: res.citations,
          }]);
          if (res.audio_reply) playReply(res.audio_reply);
          else if (voiceEnabled && res.reply) {
            // No TTS from server (e.g., short-circuit) — silent.
          }
        } catch (e) {
          setError(e?.response?.data?.detail || e?.message || "Voice failed");
        } finally {
          setVoiceBusy(false);
        }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        setError("Microphone permission was denied. You can still chat by typing.");
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        setError("No microphone was detected. You can still chat by typing.");
      } else if (name === "NotReadableError") {
        setError("Your microphone is in use by another app. Close it and try again.");
      } else {
        setError("Could not start the microphone. You can still chat by typing.");
      }
      setRecording(false);
    }
  };

  const submitLead = async () => {
    if (!session) return;
    setLeadStatus("sending");
    try {
      const res = await v.leadFn({ session_id: session.session_id, ...leadForm });
      setLeadStatus("ok");
      setMessages((m) => [...m, { role: "assistant", text: res.message, at: Date.now() }]);
      setTimeout(() => setLeadOpen(false), 1500);
    } catch (e) {
      setLeadStatus("error");
      setError(e?.response?.data?.detail || e?.message || "Could not send request");
    }
  };

  const clearChat = () => {
    if (!session || !info) return;
    setMessages([{ role: "assistant", text: session.welcome || info.welcome, at: Date.now() }]);
    setDraft("");
    setLeadOpen(false); setLeadStatus(null); setError(null);
    setQuoteState({ submitting: false, submitted: null, error: null });
  };

  // Submit an Iris-collected quote (LitLab variant) — server writes to
  // litlab_quotes + LCC, mirrors the manual /litlab/quote path.
  const submitIrisQuote = async (intent) => {
    if (!session || !intent) return;
    setQuoteState({ submitting: true, submitted: null, error: null });
    try {
      const payload = {
        session_id: session.session_id,
        name: intent.name || "Visitor",
        company: intent.company || null,
        email: intent.email || null,
        phone: intent.phone || null,
        industry: intent.industry || null,
        services: Array.isArray(intent.services) ? intent.services : [],
        sample_count: typeof intent.sample_count === "number" ? intent.sample_count : null,
        material: intent.material || null,
        turnaround: intent.turnaround || null,
        deadline: intent.deadline || null,
        notes: intent.notes || null,
        consent: true,
        language,
      };
      const res = await litlabAgentQuoteSubmit(payload);
      setQuoteState({ submitting: false, submitted: res, error: null });
      setMessages((m) => [...m, {
        role: "assistant", text: res.message || "Quote submitted.", at: Date.now(),
      }]);
    } catch (e) {
      setQuoteState({ submitting: false, submitted: null,
                       error: e?.response?.data?.detail || e?.message || "Submission failed" });
    }
  };

  if (!open) return null;

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const expired = remaining <= 0;

  return (
    <div
      role="dialog"
      aria-label="Talk to our AI Employee"
      data-testid="public-demo-dialog"
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full max-w-2xl h-[85vh] max-h-[720px] ${v.theme.shell} rounded-2xl shadow-2xl overflow-hidden border ${v.theme.shellBorder} flex flex-col`}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 ${v.theme.headerBg}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/25 backdrop-blur grid place-items-center">
              <v.IconComp className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-white font-bold leading-tight" data-testid="demo-rep-name">
                {info?.name || (variant === "litlab" ? "Iris" : "Nova")} · Live demo
              </div>
              <div className={`text-xs ${v.theme.headerSub}`}>
                {v.headerLabel}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {languagePicked && session && (
              <button
                onClick={clearChat}
                data-testid="demo-clear-btn"
                aria-label="New chat — clears the current answer, keeps the session"
                title="New chat"
                className="text-white/80 hover:text-white transition p-1.5 rounded hover:bg-white/15"
              >
                <RotateCcw size={16} />
              </button>
            )}
            <span className={`text-xs font-mono px-2 py-1 rounded ${expired ? "bg-red-500/40 text-white" : "bg-white/20 text-white"}`} data-testid="demo-timer">
              {mm}:{ss}
            </span>
            <button
              onClick={variant === "litlab" && languagePicked && session ? clearChat : onClose}
              data-testid="demo-close-btn"
              className="text-white/80 hover:text-white transition"
              aria-label={variant === "litlab" && languagePicked && session
                ? "Clear this answer — back to starter questions (tap outside to close)"
                : "Close demo"}
              title={variant === "litlab" && languagePicked && session
                ? "Back to starter questions"
                : "Close"}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Consent banner */}
        <div className={`px-5 py-2 border-b ${v.theme.consentBg} text-[11px] flex items-center gap-2`}>
          <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" />
          {info?.consent?.ai_disclosure || (
            variant === "litlab"
              ? "This is an AI assistant from L.I.T. Labs. A metallurgist reviews before binding quotes."
              : "This is an AI demo employee."
          )}
          &nbsp;·&nbsp;{info?.consent?.voice_notice || "Voice audio is not stored."}
        </div>

        {/* Language picker (shown before session starts) */}
        {!languagePicked && info?.supported_languages && (
          <div
            className={`flex-1 overflow-y-auto flex flex-col items-center justify-center px-8 py-10 text-center ${v.theme.bodyBg}`}
            data-testid="demo-language-picker"
          >
            <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${v.accentGradient} grid place-items-center mb-5 shadow-lg`}>
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-slate-900 text-xl font-bold">Choose your language</h3>
            <p className="text-slate-600 text-sm mt-2 max-w-sm">
              {info?.name || (variant === "litlab" ? "Iris" : "Nova")} will speak, transcribe, and reply in the language you pick.
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-md">
              {info.supported_languages.map((l) => (
                <button
                  key={l.code}
                  data-testid={`demo-lang-${l.code}`}
                  onClick={() => startSessionInLanguage(l.code)}
                  className={`rounded-lg border-2 ${v.theme.panelCard} text-left px-4 py-3 transition font-semibold`}
                >
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">{l.code}</div>
                  <div className="text-base font-bold text-slate-900">{l.label}</div>
                </button>
              ))}
            </div>
            {error && (
              <div className="mt-6 text-xs text-red-800 bg-red-50 border border-red-200 rounded px-3 py-1.5">
                {error}
              </div>
            )}
          </div>
        )}

        {/* Messages */}
        {languagePicked && (
        <div
          ref={scrollRef}
          className={`flex-1 overflow-y-auto px-5 py-4 space-y-3 ${v.theme.bodyBg}`}
          data-testid="demo-messages"
        >
          {messages.map((m, i) => {
            const isLastAssistant = m.role === "assistant" && (() => {
              // Find the LAST assistant message that has a quote intent.
              for (let j = messages.length - 1; j >= 0; j--) {
                const mm = messages[j];
                if (mm.role !== "assistant") continue;
                if (typeof mm.text === "string" && mm.text.includes("<QUOTE_INTENT>")) {
                  return j === i;
                }
              }
              return false;
            })();
            const { clean, intent } = m.role === "assistant" ? parseQuoteIntent(m.text) : { clean: m.text, intent: null };
            const showQuoteCard = variant === "litlab" && intent && intent.name && (intent.email || intent.phone) && isLastAssistant;
            return (
              <div key={i} className={`flex ${m.role === "visitor" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] ${m.role === "visitor" ? "" : "flex flex-col gap-2"}`}>
                  <div
                    className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed shadow-sm ${
                      m.role === "visitor"
                        ? `${v.theme.chatUser} rounded-br-sm`
                        : `${v.theme.chatAssistant} rounded-bl-sm`
                    }`}
                  >
                    {m.voice && <div className="text-[10px] uppercase tracking-wider opacity-70 mb-1">Voice</div>}
                    <div className="whitespace-pre-wrap">{clean || m.text}</div>
                    {m.citations && m.citations.length > 0 && (
                      <div className={`mt-2 text-[10px] pt-1.5 border-t ${m.role === "visitor" ? "text-white/70 border-white/20" : "text-slate-500 border-slate-200"}`}>
                        Grounded in: {m.citations.map((c) => c.title).slice(0, 3).join(" · ")}
                      </div>
                    )}
                  </div>
                  {showQuoteCard && (
                    <QuoteConfirmCard
                      intent={intent}
                      submitting={quoteState.submitting}
                      submitted={quoteState.submitted}
                      error={quoteState.error}
                      lang={language}
                      voiceMode={lastInputWasVoice}
                      onSubmit={() => submitIrisQuote(intent)}
                      onEdit={() => {
                        setDraft("");
                        setQuoteState({ submitting: false, submitted: null, error: null });
                        setMessages((mm) => [...mm, { role: "visitor", text: language === "es" ? "Cambiar los datos" : language === "fr" ? "Modifier" : "Change some details", at: Date.now() }]);
                      }}
                    />
                  )}
                </div>
              </div>
            );
          })}
          {(sending || voiceBusy) && (
            <div className="flex justify-start">
              <div className="bg-slate-800 text-slate-300 text-sm rounded-2xl px-4 py-2.5 flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {voiceBusy ? "Transcribing…" : "Thinking…"}
              </div>
            </div>
          )}

          {/* Suggested prompts (only shown initially) */}
          {messages.length <= 1 && !sending && info?.suggested_prompts && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="demo-suggestions">
              {info.suggested_prompts.map((p) => (
                <button
                  key={p}
                  onClick={() => send(p)}
                  className="text-xs px-3 py-1.5 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 transition"
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Lead capture form */}
        {languagePicked && leadOpen && (
          <div className="border-t border-white/10 bg-slate-900/80 px-5 py-3" data-testid="demo-lead-form">
            {leadStatus === "ok" ? (
              <div className="text-emerald-300 text-sm">Your demo is booked! Look for an email soon.</div>
            ) : (
              <>
                <div className="text-white text-sm font-semibold mb-2 flex items-center gap-2">
                  <PhoneCall className="w-4 h-4 text-indigo-400" /> Book your live demo
                </div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <input
                    data-testid="demo-lead-name"
                    className="bg-slate-800 text-white text-sm rounded-md px-3 py-2 border border-slate-700 outline-none focus:border-indigo-500"
                    placeholder="Your name"
                    value={leadForm.name}
                    onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                  />
                  <input
                    data-testid="demo-lead-email"
                    className="bg-slate-800 text-white text-sm rounded-md px-3 py-2 border border-slate-700 outline-none focus:border-indigo-500"
                    placeholder="Work email"
                    value={leadForm.email}
                    onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                  />
                </div>
                <input
                  className="w-full bg-slate-800 text-white text-sm rounded-md px-3 py-2 border border-slate-700 outline-none focus:border-indigo-500 mb-2"
                  placeholder="What would you like to see? (optional)"
                  value={leadForm.interest}
                  onChange={(e) => setLeadForm({ ...leadForm, interest: e.target.value })}
                />
                <div className="flex items-center justify-between gap-2">
                  <label className="text-xs text-slate-400 flex items-center gap-2">
                    <input type="checkbox" checked={leadForm.consent} onChange={(e) => setLeadForm({ ...leadForm, consent: e.target.checked })} />
                    I agree to be contacted about my demo.
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setLeadOpen(false)}
                      className="text-xs px-3 py-1.5 text-slate-300 hover:text-white transition"
                    >
                      Cancel
                    </button>
                    <button
                      data-testid="demo-lead-submit"
                      onClick={submitLead}
                      disabled={!leadForm.email || !leadForm.consent || leadStatus === "sending"}
                      className="text-xs px-3 py-1.5 rounded-md bg-indigo-500 hover:bg-indigo-400 text-white font-semibold disabled:opacity-50"
                    >
                      {leadStatus === "sending" ? "Sending…" : "Book demo"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Error toast */}
        {error && (
          <div className="mx-5 mb-2 text-xs text-red-800 bg-red-50 border border-red-200 rounded px-3 py-1.5" data-testid="demo-error">
            {error}
          </div>
        )}

        {/* Input bar */}
        {languagePicked && (
        <div className={`${v.theme.inputWrap} p-3 flex items-center gap-2`}>
          <button
            data-testid="demo-mic-btn"
            onClick={toggleMic}
            disabled={expired || voiceBusy}
            className={`w-10 h-10 rounded-full grid place-items-center transition relative ${
              recording ? "bg-red-500 text-white animate-pulse" : "bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200"
            } disabled:opacity-40`}
            aria-label={recording ? "Stop recording" : "Start voice"}
          >
            {recording ? <StopCircle className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            {showVoiceHint && !recording && (
              <span
                data-testid="voice-retry-hint"
                className="absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap text-[11px] font-semibold bg-emerald-500 text-slate-900 px-2 py-1 rounded-full shadow-lg shadow-emerald-500/30 animate-in fade-in slide-in-from-bottom-1"
              >
                Tap the mic again to record
                <span className="absolute left-1/2 -bottom-1 -translate-x-1/2 w-2 h-2 bg-emerald-500 rotate-45" />
              </span>
            )}
          </button>
          {detectedLang && (
            <div data-testid="voice-lang-pill"
              className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5"
              title={`Whisper detected: ${detectedLang}`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              {detectedLang === "es" ? "Español" : detectedLang === "fr" ? "Français" : "English"}
            </div>
          )}
          <input
            data-testid="demo-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            placeholder={expired ? "Session expired — start a new demo" : v.inputPlaceholder}
            disabled={expired}
            className={`flex-1 text-sm rounded-full px-4 py-2.5 outline-none border-2 focus:border-indigo-500 disabled:opacity-50 ${v.theme.inputBg}`}
          />
          <button
            data-testid="demo-send-btn"
            onClick={() => send()}
            disabled={!draft.trim() || sending || expired}
            className="w-10 h-10 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white grid place-items-center transition disabled:opacity-40"
            aria-label="Send"
          >
            <Send className="w-4 h-4" />
          </button>
          <button
            data-testid="demo-close-btn-inline"
            onClick={clearChat}
            className="w-10 h-10 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 grid place-items-center transition"
            aria-label={variant === "litlab"
              ? "Clear this answer — you'll go back to the starter questions on this page"
              : "Clear this answer — go back to the starter questions"}
            title="Clear answer · start a new question"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        )}

        {expired && (
          <div className="border-t border-white/10 px-5 py-3 bg-slate-950 text-center text-sm text-slate-400">
            Your demo session has ended.{" "}
            <button onClick={() => window.location.reload()} className="text-indigo-400 font-semibold underline">Reload</button>{" "}
            or{" "}
            <Link to="/audit" className="text-indigo-400 font-semibold underline">get a full audit</Link>.
          </div>
        )}
      </div>
    </div>
  );
}
